import React from 'react';
import { gql, useQuery } from '@apollo/client';
import ValueAccelerationTab from '../components/Succession/ValueAcceleration/ValueAccelerationTab';

const GET_VALUE_ACCEL_DATA = gql`
  query GetValueAccelData {
    getCurrentUser {
      id
      # Readiness vs Attractiveness totals
      diag_readiness_total
      diag_attractiveness_total
      # Profit Gap snapshot
      profit_gap_recasted_ebitda
      # Wealth/Value progress
      readiness_gap_value
      readiness_percent_to_goal
      readiness_goal_lump_sum
      value_gap_current_value
      value_gap_gap_value
      # Revenue and expenses to compute current EBITDA margin
      fee_based_revenue
      trailer_revenue
      direct_revenue
      transactional_revenue
      recurring_revenue
      non_recurring_revenue
      normalized_operating_expenses
      # Action Plan
      action_plan { id items { id status cepa_category selected_for_sprint task_template { id title } } }
      company { business_type ebitda_multiple_low ebitda_multiple_high }
      # Triggering events needed for gating prompts
      triggering_events(limit: 50) { event_type completed_at }
    }
  }
`;

const mapKanban = (items = []) => {
  const inProgress = [];
  const done = [];
  items.forEach((it) => {
    const tpl = it.task_template || {};
    const priority = 'medium';
    const card = {
      id: it.id,
      title: tpl.title || 'Task',
      description: tpl.description || '',
      priority,
      dueDate: null,
      assignee: null,
    };
    if (it.status === 'done' || it.status === 'completed') done.push(card);
    else if (it.selected_for_sprint) inProgress.push(card);
  });
  return { inProgress, done };
};

const ValueAccelerationContainer = ({ className = '', suppressPrompt = false, onReady }) => {
  const notifiedRef = React.useRef(false);

  const { data, loading, error, refetch } = useQuery(GET_VALUE_ACCEL_DATA, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      if (!notifiedRef.current) {
        notifiedRef.current = true;
        try { onReady && onReady(); } catch (_) {}
        try { setTimeout(() => window.dispatchEvent(new Event('resize')), 30); } catch (_) {}
      }
    },
  });

  // Fallback readiness in case onCompleted didn't fire
  React.useEffect(() => {
    if (!loading && data && !notifiedRef.current) {
      notifiedRef.current = true;
      try { onReady && onReady(); } catch (_) {}
      try { setTimeout(() => window.dispatchEvent(new Event('resize')), 30); } catch (_) {}
    }
  }, [loading, data, onReady]);

  // After mount, when tutorial is showing, do a gentle refetch to make sure
  // the latest triggering_events and gap data are present while overlay is open.
  React.useEffect(() => {
    if (suppressPrompt) {
      const t = setTimeout(() => { try { refetch(); } catch (_) {} }, 150);
      return () => clearTimeout(t);
    }
  }, [suppressPrompt, refetch]);

  if (error) {
    // Be resilient for new users without plan/data
    console.warn('ValueAccelerationContainer: GraphQL error', error);
  }
  const u = data?.getCurrentUser || {};

  const readinessAttractiveness = { readiness: u.diag_readiness_total, attractiveness: u.diag_attractiveness_total };
  // Radial labels from top center clockwise: Identify, Protect, Build, Harvest, Manage
  // Using available diagnostics where sensible; others as placeholders until backend provides dimensions
  const vmi = { data: [
    { subject: 'Identify', A: Math.max(1, Math.round((u.diag_readiness_total || 6) / 5)), fullMark: 6 },
    { subject: 'Protect', A: 3, fullMark: 6 },
    { subject: 'Build', A: Math.max(1, Math.round((u.diag_attractiveness_total || 6) / 5)), fullMark: 6 },
    { subject: 'Harvest', A: 3, fullMark: 6 },
    { subject: 'Manage', A: 3, fullMark: 6 },
  ]};
  // Profit distribution inputs (mirror ProfitGapResults)
  const businessType = u?.company?.business_type || 'wealth_rj';
  const totalRevenue = businessType === 'p_and_c'
    ? Number(u.recurring_revenue || 0) + Number(u.non_recurring_revenue || 0)
    : Number(u.fee_based_revenue || 0) + Number(u.trailer_revenue || 0) + Number(u.direct_revenue || 0) + Number(u.transactional_revenue || 0);
  const recastedEbitda = (totalRevenue || 0) - Number(u.normalized_operating_expenses || 0);
  const ebitdaMarginCurrent = totalRevenue > 0 ? (recastedEbitda / totalRevenue) : 0;
  const lowMargin = 0.15;
  const highMargin = 0.45;
  const avgMargin = 0.228;
  const profitDistribution = { low: lowMargin, avg: avgMargin, high: highMargin, marker: ebitdaMarginCurrent };
  const benchmarkHigh = totalRevenue * highMargin;
  const profitGapHigh = benchmarkHigh - recastedEbitda; // match ProfitGapResults calculation
  const profitPct = benchmarkHigh > 0 ? Math.round(Math.max(0, Math.min(100, (profitGapHigh / benchmarkHigh) * 100))) : 0;
  const wealthGoal = Number(u.readiness_goal_lump_sum || 0);
  const wealthGapValue = Number(u.readiness_gap_value || 0);
  const valueGapValue = Number(u.value_gap_gap_value || 0);
  const wealthPct = wealthGoal > 0 ? Math.round((wealthGapValue / wealthGoal) * 100) : 0;
  const valuePct = wealthGoal > 0 ? Math.round((valueGapValue / wealthGoal) * 100) : 0;

  const wealthProgress = { label: 'Wealth Gap', percent: wealthPct, amount: wealthGapValue };
  const valueProgress = { label: 'Value Gap', percent: valuePct, amount: valueGapValue };
  const sprint = mapKanban(u?.action_plan?.items || []);

  return (
    <ValueAccelerationTab
      className={className}
      currentUser={u}
      vmi={vmi}
      readinessAttractiveness={readinessAttractiveness}
      profitDistribution={profitDistribution}
      profitProgress={{ label: 'Profit Gap', percent: profitPct, amount: profitGapHigh }}
      wealthProgress={wealthProgress}
      valueProgress={valueProgress}
      sprint={sprint}
      suppressPrompt={suppressPrompt}
    />
  );
};

export default ValueAccelerationContainer;


